import _axios from "axios";

import { axios } from "@/client/libs/axios";
import { refresh } from "@/client/services/auth/refresh";

export type AiAction =
  | "improve-writing"
  | "fix-grammar"
  | "change-tone"
  | "generate-summary"
  | "generate-experience"
  | "generate-education"
  | "generate-skills"
  | "generate-project"
  | "generate-section"
  | "expand-content"
  | "quantify-achievements"
  | "tailor-to-job"
  | "analyze-job-match"
  | "review-resume"
  | "suggest-improvements"
  | "chat"
  | "draft-from-text";

export type AiTone =
  | "casual"
  | "professional"
  | "confident"
  | "friendly"
  | "concise"
  | "enthusiastic";

export interface AiRequest {
  action: AiAction;
  content?: string;
  context?: Record<string, unknown>;
  tone?: AiTone;
  jobDescription?: string;
  sectionType?: string;
  chatHistory?: Array<{ role: "user" | "assistant"; content: string }>;
}

export interface RateLimitStatus {
  used: number;
  limit: number;
  remaining: number;
  warning: string | null;
  blocked: boolean;
}

export interface AiResponse {
  result: string;
  suggestions?: string[];
  score?: number;
  metadata?: Record<string, unknown>;
  rateLimit?: RateLimitStatus;
  callId?: string;
  /** False when result is a terminal message, not content to apply. */
  ok?: boolean;
}

export interface AiStatusResponse {
  enabled: boolean;
  rateLimit?: RateLimitStatus;
}

export const getAiStatus = async (): Promise<AiStatusResponse> => {
  const response = await axios.get<AiStatusResponse>("/ai/status");
  return response.data;
};

export const processAi = async (request: AiRequest): Promise<AiResponse> => {
  const response = await axios.post<AiResponse>("/ai/process", request);
  return response.data;
};

export const reportAiOutcome = (callId: string | undefined, outcome: "used" | "continued"): void => {
  if (!callId) return;
  void axios.post(`/ai/calls/${callId}/outcome`, { outcome }).catch(() => undefined);
};

type StreamEvent =
  | { type: "chunk"; text: string }
  | { type: "retry" }
  | { type: "done"; rateLimit?: RateLimitStatus; callId?: string }
  | { type: "error"; result: string; rateLimit?: RateLimitStatus; callId?: string };

export const processAiStream = async (
  request: AiRequest,
  onChunk: (text: string) => void,
  options?: { onRetry?: () => void },
): Promise<AiResponse> => readAiStream(request, onChunk, options, false);

const readAiStream = async (
  request: AiRequest,
  onChunk: (text: string) => void,
  options: { onRetry?: () => void } | undefined,
  retried: boolean,
): Promise<AiResponse> => {
  const response = await fetch("/api/ai/process-stream", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(request),
  });

  if ((response.status === 401 || response.status === 403) && !retried) {
    await refresh(_axios.create({ baseURL: "/api", withCredentials: true }));
    return readAiStream(request, onChunk, options, true);
  }

  if (!response.ok || !response.body) {
    throw new Error("AI stream failed");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let full = "";
  let rateLimit: RateLimitStatus | undefined;
  let callId: string | undefined;

  const handleEvent = (event: StreamEvent) => {
    if (event.type === "retry") {
      full = "";
      onChunk("");
      options?.onRetry?.();
      return;
    }
    if (event.type === "chunk") {
      full += event.text;
      onChunk(full);
      return;
    }
    if (event.type === "done") {
      rateLimit = event.rateLimit;
      callId = event.callId;
      return;
    }
    if (event.type === "error") {
      rateLimit = event.rateLimit;
      callId = event.callId;
      throw new Error(event.result);
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";
    for (const part of parts) {
      const line = part.split("\n").find((entry) => entry.startsWith("data: "));
      if (!line) continue;
      handleEvent(JSON.parse(line.slice(6)) as StreamEvent);
    }
  }

  if (buffer.trim()) {
    const line = buffer.split("\n").find((entry) => entry.startsWith("data: "));
    if (line) handleEvent(JSON.parse(line.slice(6)) as StreamEvent);
  }

  return { result: full, rateLimit, callId };
};
